import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Eye, ArrowRight } from 'lucide-react';
import { useAnalytics } from '../hooks/useAnalytics';

import NavBar from './NavBar';
import MobileHeroSection from './MobileHeroSection';

// import ProblemFlowchart from './problemstatement/ProblemFlowchart';
import TabbedWorkflowSection from './TabbedWorkflowSection';
import AsciiTechSection from './AsciiTechSection';

import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import ResponsiveFooter from './ResponsiveFooter';


// Apple-inspired color palette
const colors = {
  // Primary accent - deeper blue for a more formal, enterprise feel
  accent: '#1e40af',
  accentHover: '#1e3a8a',
  accentLight: 'rgba(30, 64, 175, 0.1)',
  
  // Neutral tones
  white: '#ffffff',
  gray50: '#f9f9f9',
  gray100: '#f3f3f3',
  gray200: '#e6e6e6',
  gray300: '#d1d1d1',
  gray500: '#8e8e8e',
  gray700: '#636363',
  gray900: '#333333',
  
  // Status colors (more subtle)
  success: 'rgb(52, 199, 89)',
  warning: 'rgb(255, 149, 0)',
  error: 'rgb(255, 59, 48)',
  successLight: 'rgba(52, 199, 89, 0.1)',
  warningLight: 'rgba(255, 149, 0, 0.1)',
  errorLight: 'rgba(255, 59, 48, 0.1)',
};

// Global font styles
const fontStyles = {
  fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
  heading: {
    fontWeight: 600, // Semi-bold for headings
    letterSpacing: '-0.03em',
  },
  body: {
    fontWeight: 400, // Regular for body text
    letterSpacing: '-0.01em',
  },
  medium: {
    fontWeight: 500, // Medium weight
  },
  light: {
    fontWeight: 300, // Light weight
  }
};

// Animation variants for staggered animations
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { type: 'spring', stiffness: 300, damping: 24 }
  },
};



const LandingPage = () => {
  const navigate = useNavigate();
  const { trackEngagement } = useAnalytics();
  const [isMobile, setIsMobile] = useState(false);
  
  // Intersection Observer hooks for scroll animations
  const [heroRef, heroInView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  // Check if viewport is mobile
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Initial check
    checkIsMobile();
    
    // Add resize listener
    window.addEventListener('resize', checkIsMobile);
    
    // Cleanup
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);



  return (
    <div className="min-h-screen bg-white text-gray-900 flex flex-col overflow-hidden" 
         style={{
           color: colors.gray900,
           fontFamily: fontStyles.fontFamily // Apply font family globally
         }}>
      {/* Navigation */}
      <NavBar 
        activePage=""
      />
      
      {/* Conditional rendering of hero section based on device */}
      {isMobile ? (
        /* Mobile: Keep original hero with animations and add bottom notice/link */
        <div className="relative">
          <MobileHeroSection />
          {/* Bottom mobile notice with exact wording */}
          <div className="absolute bottom-6 left-0 right-0 px-6">
            <div className="max-w-md w-full mx-auto text-left">
              <p className="text-sm" style={{color: colors.gray700}}>
                Dashboard isn't mobile-friendly as of now
              </p>
              <p className="text-sm mt-1" style={{color: colors.gray700}}>
                would highly recommend using the web product to make an account and go forward.
              </p>
            </div>
          </div>
        </div>
      ) : (
        /* Original Hero Section for desktop - REFACTORED WITH CSS GRID */
        <motion.div 
          className="min-h-screen relative overflow-hidden"
          ref={heroRef}
          variants={containerVariants}
          initial="hidden"
          animate={heroInView ? "visible" : "hidden"}
          style={{
            background: `radial-gradient(circle at 70% 20%, ${colors.accentLight} 0%, rgba(255, 255, 255, 0) 25%)`,
            fontFamily: fontStyles.fontFamily,
          }}
        >
          {/* CSS Grid Container for Responsive Layout */}
          <div className="absolute inset-0 grid grid-rows-[auto_1fr_auto] pt-28 md:pt-32 pb-24 px-6">
                        {/* TOP SECTION: Text Content */}
            <div className="max-w-6xl mx-auto w-full grid grid-cols-12 gap-6 items-start mb-4 lg:mb-6 xl:mb-8 z-20">
              {/* Left Column: Main Heading */}
              <motion.div
                className="col-span-12 lg:col-span-6 xl:col-span-5"
                variants={itemVariants}
              >
                <motion.h1 
                  className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold"
                  style={{
                    lineHeight: '1.08',
                    letterSpacing: '-0.04em',
                    fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
                    marginTop: '0.25rem'
                  }}
                  variants={itemVariants}
                >
                  <div style={{ color: '#333333', fontWeight: 600 }}>Contracts.</div>
                  <div style={{ color: '#1e40af', fontWeight: 600 }}>Simplified.</div> 
                </motion.h1>
              </motion.div>

              {/* Right Column: Logo and Subheading - Positioned Lower */}
              <div className="col-span-12 lg:col-span-6 xl:col-span-7 lg:pl-8 mt-8 lg:mt-12 xl:mt-16">
                {/* Logo positioned above subheading */}
                <motion.div
                  className="flex justify-start lg:justify-end mb-4"
                  variants={itemVariants}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <img src="/6.png" alt="SignPact Logo" className="h-6 md:h-8 opacity-90" />
                </motion.div>

                {/* Right-positioned description container */}
                <motion.div
                  className="max-w-md lg:ml-auto"
                  variants={itemVariants}
                >
                  <motion.p 
                    className="text-base md:text-lg xl:text-xl text-left lg:text-right"
                    style={{
                      color: colors.gray700,
                      lineHeight: '1.5',
                      letterSpacing: '-0.01em',
                      marginTop: '0.5rem',
                      ...fontStyles.body
                    }}
                    variants={itemVariants}
                  >
                    AI-native e-signature and contract generation tool designed for small businesses and freelancers.
                  </motion.p>
                </motion.div>
              </div>
            </div>
            
            {/* MIDDLE SECTION: Video + Documents Container with Proper Layering */}
            <div className="flex flex-col items-center justify-center w-full max-w-6xl mx-auto relative -mt-4 lg:-mt-8" style={{ perspective: '1000px' }}>
                            {/* Main Product Video - positioned back to original location */}
              <motion.div 
                className="w-full opacity-70 pointer-events-none mb-0 lg:mb-2 z-0"
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 0.7, y: 0 }}
                transition={{ duration: 0.8, delay: 0.5 }}
              >
              <div className="rounded-2xl overflow-hidden mx-auto shadow-2xl" 
                   style={{
                     boxShadow: '0 15px 50px -12px rgba(0, 0, 0, 0.15), 0 0 30px -5px rgba(0, 0, 0, 0.08)',
                     filter: 'blur(0.5px)',
                     border: '1px solid rgba(255, 255, 255, 0.2)',
                     backdropFilter: 'blur(8px)',
                   }}>
                
                {/* Mac-style window chrome - ultra thin */}
                <div className="h-5 flex items-center px-3 border-b relative" 
                     style={{
                       background: 'linear-gradient(to right, #f8fafc, #f1f5f9)',
                       borderColor: 'rgba(226, 232, 240, 0.8)',
                       backdropFilter: 'blur(12px)'
                     }}>
                  {/* Thin colored line at top */}
                  <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-400 to-indigo-500"></div>
                  
                  {/* Mac-style window control buttons - smaller */}
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 rounded-full" style={{backgroundColor: colors.error}}></div>
                    <div className="w-2 h-2 rounded-full" style={{backgroundColor: colors.warning}}></div>
                    <div className="w-2 h-2 rounded-full" style={{backgroundColor: colors.success}}></div>
                  </div>
                </div>
                
                <video
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="w-full h-full object-cover"
                  style={{
                     aspectRatio: '16/9',
                     filter: 'brightness(0.9) contrast(1.1) saturate(0.9)',
                       minHeight: '300px',
                       maxHeight: '500px',
                   }}
                  onError={(e) => {
                    console.warn('Hero video failed to load:', e);
                    // Hide the video container on error
                    e.target.parentElement.parentElement.style.display = 'none';
                  }}
                >
                  <source src="/finalherosection.mp4" type="video/mp4" />
                  {/* Fallback content */}
                  <div className="w-full h-full bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
                    <div className="text-center p-8">
                      <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                        <FileText size={32} className="text-blue-600" />
              </div>
                      <p className="text-gray-600">Experience the future of document management</p>
                      </div>
                    </div>
                </video>
              </div>
            </motion.div>
            
          {/* Document Preview Collection - exactly like original positioning */}
          <div className="absolute w-full h-full inset-0 pointer-events-none" style={{ perspective: '1000px' }}>
            {/* Secondary documents (blurred in background) - EXACT ORIGINAL POSITIONING */}
            <motion.div 
              className="absolute w-[65%] right-[-10%] top-[60%] opacity-40 blur-[1px] pointer-events-none z-0"
              initial={{ opacity: 0, y: 80, x: 30 }}
              animate={{ opacity: 0.4, y: 20, x: 0 }}
              transition={{ duration: 1, delay: 0.7 }}
            >
              <div className="bg-white rounded-xl shadow-lg overflow-hidden" 
                   style={{
                     boxShadow: '0 15px 30px -10px rgba(0, 0, 0, 0.15), 0 0 10px rgba(0, 0, 0, 0.05)',
                     border: '1px solid rgba(240, 240, 250, 0.6)',
                     minHeight: '160px'
                   }}>
                <div className="h-10 flex items-center px-4 bg-gradient-to-r from-slate-50 to-blue-50 border-b" 
                     style={{borderColor: 'rgba(226, 232, 240, 0.8)'}}>
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-400 to-blue-500"></div>
                  <div className="flex space-x-1.5 mr-3">
                    <div className="w-2.5 h-2.5 rounded-full" style={{backgroundColor: colors.error}}></div>
                    <div className="w-2.5 h-2.5 rounded-full" style={{backgroundColor: colors.warning}}></div>
                    <div className="w-2.5 h-2.5 rounded-full" style={{backgroundColor: colors.success}}></div>
                  </div>
                  <div className="text-xs font-medium ml-2" style={{color: colors.gray700}}>NDA - SignPact</div>
                </div>
                <div className="p-4">
                  <div className="space-y-2 text-[8px] text-gray-700 leading-tight">
                    <div className="font-semibold text-[9px] mb-1">NON-DISCLOSURE AGREEMENT</div>
                    <div className="text-gray-600">This Non-Disclosure Agreement ("Agreement") is entered into as of March 15, 2024, between TechCorp Inc., a Delaware corporation ("Disclosing Party"), and ConsultCo LLC, a California limited liability company ("Receiving Party").</div>
                    <div className="mt-2">
                      <div className="font-medium text-[8px]">1. CONFIDENTIAL INFORMATION</div>
                      <div className="text-gray-600 pl-2">The term "Confidential Information" shall mean any and all proprietary information, technical data, trade secrets, know-how, research, product plans, products, services, customers, customer lists, markets, software, developments, inventions, processes, formulas, technology, designs, drawings, engineering, hardware configuration information...</div>
                    </div>
                    <div className="mt-2">
                      <div className="font-medium text-[8px]">2. OBLIGATIONS OF RECEIVING PARTY</div>
                      <div className="text-gray-600 pl-2">Receiving Party agrees to hold and maintain the Confidential Information in strict confidence and to take all reasonable precautions to protect such Confidential Information...</div>
                    </div>
                    <div className="mt-2">
                      <div className="font-medium text-[8px]">3. TERM</div>
                      <div className="text-gray-600 pl-2">This Agreement shall remain in effect for a period of five (5) years from the date first written above, unless earlier terminated...</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
            
            {/* Document on left */}
            <motion.div 
              className="absolute w-[50%] left-[5%] top-[57%] opacity-30 blur-[1.5px] pointer-events-none z-0"
              initial={{ opacity: 0, y: 70, x: -20 }}
              animate={{ opacity: 0.3, y: 10, x: 0 }}
              transition={{ duration: 1, delay: 0.9 }}
            >
              <div className="bg-white rounded-xl shadow-lg overflow-hidden" 
                   style={{
                     boxShadow: '0 10px 25px -10px rgba(0, 0, 0, 0.1), 0 0 8px rgba(0, 0, 0, 0.03)',
                     border: '1px solid rgba(240, 240, 250, 0.5)',
                     minHeight: '140px'
                   }}>
                <div className="h-8 flex items-center px-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b" 
                     style={{borderColor: 'rgba(226, 232, 240, 0.8)'}}>
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-green-400 to-blue-400"></div>
                  <div className="flex space-x-1.5 mr-3">
                    <div className="w-2 h-2 rounded-full" style={{backgroundColor: colors.error}}></div>
                    <div className="w-2 h-2 rounded-full" style={{backgroundColor: colors.warning}}></div>
                    <div className="w-2 h-2 rounded-full" style={{backgroundColor: colors.success}}></div>
                  </div>
                  <div className="text-xs font-medium ml-2" style={{color: colors.gray700}}>Invoice #2023-42</div>
                </div>
                <div className="p-3">
                  <div className="space-y-1 text-[7px] text-gray-700 leading-tight">
                    <div className="font-semibold text-[8px] text-center mb-1">INVOICE #2024-042</div>
                    <div className="flex justify-between border-b border-gray-200 pb-1">
                      <span className="font-medium">From:</span>
                      <span className="font-medium">To:</span>
                    </div>
                    <div className="flex justify-between text-[6px]">
                      <div>
                        <div>DevStudio Pro LLC</div>
                        <div>123 Tech Avenue</div>
                        <div>San Francisco, CA 94105</div>
                      </div>
                      <div className="text-right">
                        <div>GlobalCorp Industries</div>
                        <div>456 Business Blvd</div>
                        <div>New York, NY 10001</div>
                      </div>
                    </div>
                    <div className="mt-2 border-t border-gray-200 pt-1">
                      <div className="flex justify-between font-medium">
                        <span>Description</span>
                        <span>Amount</span>
                      </div>
                      <div className="flex justify-between text-[6px]">
                        <span>AI Development Services (March 2024)</span>
                        <span>$12,500.00</span>
                      </div>
                      <div className="flex justify-between text-[6px]">
                        <span>System Integration (40 hours)</span>
                        <span>$6,000.00</span>
                      </div>
                      <div className="flex justify-between font-semibold border-t border-gray-300 pt-1">
                        <span>Total Due:</span>
                        <span>$18,500.00</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
            
            {/* Far left document */}
            <motion.div 
              className="absolute w-[45%] left-[-12%] top-[76%] opacity-25 blur-[2px] pointer-events-none z-0"
              initial={{ opacity: 0, y: 90, x: -50 }}
              animate={{ opacity: 0.25, y: 0, x: 0 }}
              transition={{ duration: 1.2, delay: 1.1 }}
            >
              <div className="bg-white rounded-lg shadow-md overflow-hidden" 
                   style={{
                     boxShadow: '0 8px 15px -10px rgba(0, 0, 0, 0.15)',
                     border: '1px solid rgba(240, 240, 250, 0.4)',
                     minHeight: '120px'
                   }}>
                <div className="h-7 flex items-center px-3 bg-gradient-to-r from-purple-50 to-blue-50 border-b" 
                     style={{borderColor: 'rgba(226, 232, 240, 0.8)'}}>
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-400 to-blue-500"></div>
                  <div className="flex space-x-1 mr-2">
                    <div className="w-1.5 h-1.5 rounded-full" style={{backgroundColor: colors.error}}></div>
                    <div className="w-1.5 h-1.5 rounded-full" style={{backgroundColor: colors.warning}}></div>
                    <div className="w-1.5 h-1.5 rounded-full" style={{backgroundColor: colors.success}}></div>
                  </div>
                  <div className="text-xs font-medium ml-2" style={{color: colors.gray700}}>Contract Amendment</div>
                </div>
                <div className="p-2">
                  <div className="space-y-1 text-[6px] text-gray-700 leading-tight">
                    <div className="font-semibold text-[7px] mb-1">CONTRACT AMENDMENT #3</div>
                    <div className="text-gray-600">Amendment to Master Service Agreement dated January 15, 2024 between TechFlow Solutions Inc. and Enterprise Systems Corp.</div>
                    <div className="mt-1">
                      <div className="font-medium text-[6px]">SECTION 4.2 - COMPENSATION</div>
                      <div className="text-gray-600 pl-1">The hourly rate for senior developers is hereby increased from $180/hour to $195/hour, effective April 1, 2024.</div>
                    </div>
                    <div className="mt-1">
                      <div className="font-medium text-[6px]">SECTION 7.1 - DELIVERABLES</div>
                      <div className="text-gray-600 pl-1">Additional milestone for AI integration module added to project scope. Estimated completion: Q3 2024.</div>
                    </div>
                    <div className="mt-1">
                      <div className="font-medium text-[6px]">SECTION 9.3 - TERM EXTENSION</div>
                      <div className="text-gray-600 pl-1">Contract term extended by six (6) months through December 31, 2024, with option for additional renewal.</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
            
            {/* Far right document */}
            <motion.div 
              className="absolute w-[45%] right-[7%] top-[85%] opacity-20 blur-[2.5px] pointer-events-none z-0"
              initial={{ opacity: 0, y: 100, x: 60 }}
              animate={{ opacity: 0.2, y: 5, x: 0 }}
              transition={{ duration: 1.3, delay: 1.2 }}
            >
              <div className="bg-white rounded-lg shadow-md overflow-hidden" 
                   style={{
                     boxShadow: '0 8px 20px -12px rgba(0, 0, 0, 0.2)',
                     border: '1px solid rgba(240, 240, 250, 0.4)',
                     minHeight: '130px'
                   }}>
                <div className="h-7 flex items-center px-3 bg-gradient-to-r from-blue-50 to-cyan-50 border-b" 
                     style={{borderColor: 'rgba(226, 232, 240, 0.8)'}}>
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-sky-400 to-cyan-500"></div>
                  <div className="flex space-x-1 mr-2">
                    <div className="w-1.5 h-1.5 rounded-full" style={{backgroundColor: colors.error}}></div>
                    <div className="w-1.5 h-1.5 rounded-full" style={{backgroundColor: colors.warning}}></div>
                    <div className="w-1.5 h-1.5 rounded-full" style={{backgroundColor: colors.success}}></div>
                  </div>
                  <div className="text-xs font-medium ml-2" style={{color: colors.gray700}}>SOW - Project X</div>
                </div>
                <div className="p-2">
                  <div className="space-y-1 text-[6px] text-gray-700 leading-tight">
                    <div className="font-semibold text-[7px] mb-1">STATEMENT OF WORK - PROJECT X</div>
                    <div className="text-gray-600">Cloud Migration & Modernization Project for DataCorp Solutions - Phase 2 Implementation</div>
                    <div className="mt-1">
                      <div className="font-medium text-[6px]">PROJECT SCOPE</div>
                      <div className="text-gray-600 pl-1">• Migration of legacy systems to AWS cloud infrastructure</div>
                      <div className="text-gray-600 pl-1">• Implementation of microservices architecture</div>
                      <div className="text-gray-600 pl-1">• Database optimization and performance tuning</div>
                    </div>
                    <div className="mt-1">
                      <div className="font-medium text-[6px]">DELIVERABLES</div>
                      <div className="text-gray-600 pl-1">• Cloud architecture documentation</div>
                      <div className="text-gray-600 pl-1">• Migrated applications (12 services)</div>
                      <div className="text-gray-600 pl-1">• Performance testing reports</div>
                    </div>
                    <div className="mt-1 flex justify-between font-medium">
                      <span>Timeline: 16 weeks</span>
                      <span>Budget: $245,000</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
            </div>
            
          {/* Premium Buttons - positioned at bottom center of hero section */}
          <motion.div 
            className="absolute bottom-16 left-0 right-0 flex flex-col sm:flex-row justify-center items-center gap-6 z-30"
            variants={itemVariants}
          >
            {/* Primary Button - Get Started - Enhanced Premium Style */}
            <motion.button 
              onClick={() => {
                trackEngagement('click', 'get_started_hero', 'hero', {
                  button_text: 'Get Started Free',
                  position: 'primary_cta'
                });
                navigate('/signup');
              }}
              className="px-8 py-4 font-medium flex items-center justify-center transition-all duration-300 ease-out relative overflow-hidden group"
              style={{
                borderRadius: '20px',
                background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentHover} 100%)`,
                color: colors.white,
                boxShadow: '0 10px 30px rgba(30, 64, 175, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                ...fontStyles.medium,
                letterSpacing: '-0.01em',
                fontSize: '18px',
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                border: `1px solid rgba(255, 255, 255, 0.1)`
              }}
              whileHover={{ 
                scale: 1.04,
                y: -2
              }}
              whileTap={{ scale: 0.98 }}
              variants={itemVariants}
              onMouseEnter={(e) => {
                e.target.style.boxShadow = '0 15px 40px rgba(30, 64, 175, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.3)';
                e.target.style.filter = 'brightness(1.05)';
              }}
              onMouseLeave={(e) => {
                e.target.style.boxShadow = '0 10px 30px rgba(30, 64, 175, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.2)';
                e.target.style.filter = 'brightness(1)';
              }}
            >
              {/* Shimmer effect */}
              <div 
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{
                  background: 'linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.2) 50%, transparent 70%)',
                  animation: 'shimmer 2s infinite'
                }}
              />
              <span className="relative z-10 mr-3">Get Started Free</span>
              <ArrowRight size={20} className="relative z-10 transition-transform duration-300 group-hover:translate-x-1" />
            </motion.button>

            {/* Secondary Button - Book a Demo - Premium Glass Effect */}
            <motion.button 
              onClick={() => {
                trackEngagement('click', 'book_demo_hero', 'hero', {
                  button_text: 'Book a Demo',
                  position: 'secondary_cta',
                  external_link: true
                });
                window.open('https://cal.com/ranvirdeshmukh/book-a-demo-signpact', '_blank');
              }}
              className="px-7 py-4 font-medium flex items-center justify-center transition-all duration-300 ease-out relative overflow-hidden group border"
              style={{
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.9) 100%)',
                borderColor: 'rgba(30, 64, 175, 0.15)',
                color: colors.gray700,
                fontSize: '16px',
                borderRadius: '20px',
                fontWeight: 500,
                letterSpacing: '-0.01em',
                boxShadow: '0 8px 25px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(12px)',
                fontFamily: fontStyles.fontFamily
              }}
              whileHover={{ 
                scale: 1.03,
                y: -2
              }}
              whileTap={{ scale: 0.98 }}
              variants={itemVariants}
              onMouseEnter={(e) => {
                e.target.style.background = 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.95) 100%)';
                e.target.style.borderColor = 'rgba(30, 64, 175, 0.25)';
                e.target.style.color = colors.accent;
                e.target.style.boxShadow = '0 12px 35px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.9)';
                e.target.style.filter = 'brightness(1.02)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.9) 100%)';
                e.target.style.borderColor = 'rgba(30, 64, 175, 0.15)';
                e.target.style.color = colors.gray700;
                e.target.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.8)';
                e.target.style.filter = 'brightness(1)';
              }}
            >
              {/* Subtle gradient overlay */}
              <div 
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                style={{
                  background: 'linear-gradient(135deg, rgba(30, 64, 175, 0.02) 0%, transparent 50%)',
                  borderRadius: '20px'
                }}
              />
              <span className="relative z-10 mr-3">Book a Demo</span>
              <Eye size={18} className="relative z-10 transition-transform duration-300 group-hover:scale-110" />
            </motion.button>
          </motion.div>


          
          {/* Close CSS Grid Container */}
          </div>

          {/* Enhanced Professional Fade Effect - Starts lower to preserve document visibility */}
          <motion.div 
            className="absolute bottom-0 left-0 w-full h-48 z-10 pointer-events-none"
            style={{
              background: `linear-gradient(to bottom, 
                rgba(255, 255, 255, 0) 0%, 
                rgba(255, 255, 255, 0) 20%,
                rgba(255, 255, 255, 0.4) 50%,
                rgba(255, 255, 255, 0.8) 75%,
                rgba(255, 255, 255, 1) 100%)`,
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.5 }}
          />
          
          {/* Secondary fade layer for extra smoothness - Shorter and more subtle */}
          <motion.div 
            className="absolute bottom-0 left-0 w-full h-32 z-9 pointer-events-none"
            style={{
              background: `linear-gradient(to bottom, 
                rgba(255, 255, 255, 0) 0%, 
                rgba(255, 255, 255, 0.3) 40%,
                rgba(255, 255, 255, 1) 100%)`,
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2, delay: 1.7 }}
          />
        </motion.div>
      )}
      
      {/* Section Divider - Rail-style horizontal lines */}
      {/* <div className="relative w-full">
        <div 
          className="w-full"
          style={{
            borderTop: '1.5px solid rgba(30, 64, 175, 0.25)'
          }}
        />
        {/* Diagonal hatch pattern in the gap */}
        {/* <div 
          className="w-full"
          style={{
            height: '8px',
            background: 'transparent'
          }}
        />
        {/* Double rail effect - second line below */}
        {/* <div 
          className="w-full"
          style={{
            borderTop: '1.5px solid rgba(30, 64, 175, 0.25)'
          }}
        />
      </div> */}
      
      {/* Problem Flowchart Section */}
      {/* <ProblemFlowchart /> */}
      
      {/* Section Divider - Rail-style horizontal lines */}
      <div className="relative w-full">
        <div 
          className="w-full"
          style={{
            borderTop: '1.5px solid rgba(30, 64, 175, 0.25)'
          }}
        />
        {/* Diagonal hatch pattern in the gap */}
        <div 
          className="w-full relative overflow-hidden"
          style={{
            height: '8px'
          }}
        >
          {/* Left half - matches GridScaffold left gutter (135deg) */}
          <div 
            className="absolute inset-y-0 left-0"
            style={{
              width: '50%',
              backgroundImage: `
                repeating-linear-gradient(90deg,
                  rgba(30, 64, 175, 0.11) 0,
                  rgba(30, 64, 175, 0.11) 1px,
                  transparent 1px,
                  transparent 32px),
                repeating-linear-gradient(0deg,
                  rgba(30, 64, 175, 0.077) 0,
                  rgba(30, 64, 175, 0.077) 1px,
                  transparent 1px,
                  transparent 32px),
                repeating-linear-gradient(135deg,
                  rgba(30, 64, 175, 0.055) 0,
                  rgba(30, 64, 175, 0.055) 1px,
                  transparent 1px,
                  transparent 19px)`
              ,
              backgroundPosition: '6px 0, 6px 0, 6px 0'
            }}
          />
          {/* Right half - mirrors GridScaffold right gutter (-135deg) */}
          <div 
            className="absolute inset-y-0 right-0"
            style={{
              width: '50%',
              backgroundImage: `
                repeating-linear-gradient(90deg,
                  rgba(30, 64, 175, 0.11) 0,
                  rgba(30, 64, 175, 0.11) 1px,
                  transparent 1px,
                  transparent 32px),
                repeating-linear-gradient(0deg,
                  rgba(30, 64, 175, 0.077) 0,
                  rgba(30, 64, 175, 0.077) 1px,
                  transparent 1px,
                  transparent 32px),
                repeating-linear-gradient(-136deg,
                  rgba(30, 64, 175, 0.055) 0,
                  rgba(30, 64, 175, 0.055) 1px,
                  transparent 1px,
                  transparent 19px)`
              ,
              backgroundPosition: '-6px 0, -6px 0, -6px 0'
            }}
          />
        </div>
        {/* Double rail effect - second line below */}
        <div 
          className="w-full"
          style={{
            borderTop: '1.5px solid rgba(30, 64, 175, 0.25)'
          }}
        />
      </div>
      
      {/* New Tabbed Workflow Section */}
      <TabbedWorkflowSection />
      <AsciiTechSection />
      
      {/* Section Divider - Rail-style horizontal lines */}
      <div className="relative w-full">
        <div 
          className="w-full"
          style={{
            borderTop: '1.5px solid rgba(30, 64, 175, 0.25)'
          }}
        />
        {/* Gap between double rails */}
        <div 
          className="w-full"
          style={{
            height: '8px',
            background: 'transparent'
          }}
        />
        {/* Double rail effect - second line */}
        <div 
          className="w-full"
          style={{
            borderTop: '1.5px solid rgba(30, 64, 175, 0.25)'
          }}
        />
      </div>
      
      {/* Footer */}
      <ResponsiveFooter />

    </div>
  );
};

export default LandingPage;